import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal } from 'lucide-react'

export interface RowAction {
  label: string
  onClick: () => void
  variant?: 'default' | 'danger'
}

interface RowActionsProps {
  actions: RowAction[]
  triggerClassName?: string
}

interface DropdownPos {
  top: number
  right: number
}

export default function RowActions({ actions, triggerClassName }: RowActionsProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<DropdownPos | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)

  const openDropdown = () => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    setPos({
      top: rect.bottom + window.scrollY + 4,
      right: window.innerWidth - rect.right,
    })
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (
        dropRef.current && !dropRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={openDropdown}
        className={triggerClassName ?? 'flex items-center justify-center w-7 h-7 rounded hover:bg-zinc-700 text-zinc-500 hover:text-white transition-colors'}
      >
        <MoreHorizontal size={15} />
      </button>

      {open && pos && createPortal(
        <div
          ref={dropRef}
          style={{ top: pos.top, right: pos.right, position: 'absolute' }}
          className="z-[9999] min-w-[130px] bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden"
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => { setOpen(false); action.onClick() }}
              className={`w-full text-left px-4 py-2.5 text-xs transition-colors hover:bg-zinc-700 ${
                action.variant === 'danger'
                  ? 'text-red-400 hover:text-red-300'
                  : 'text-zinc-300 hover:text-white'
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}
